import { supabase } from '../lib/supabase';

export const kpiTrackingService = {
  // Get real-time KPI metrics
  async getKPIMetrics() {
    try {
      const [travelSpend, complianceRates, bookingTrends, budgetUtilization, approvalTimes] = await Promise.all([
        this.getTravelSpendKPI(),
        this.getComplianceRatesKPI(),
        this.getBookingTrendsKPI(),
        this.getBudgetUtilizationKPI(),
        this.getApprovalProcessingTimesKPI()
      ]);

      return {
        travelSpend,
        complianceRates,
        bookingTrends,
        budgetUtilization,
        approvalTimes
      };
    } catch (error) {
      console.error('Error fetching KPI metrics:', error);
      throw error;
    }
  },

  // Travel Spend KPI
  async getTravelSpendKPI() {
    try {
      // Get total spend from bookings
      const { data: bookings, error: bookingsError } = await supabase
        ?.from('travel_bookings')
        ?.select('cost, created_at, status')
        ?.eq('status', 'confirmed');

      if (bookingsError) throw bookingsError;

      const totalSpend = bookings?.reduce((sum, booking) => sum + (booking?.cost || 0), 0) || 0;
      
      // Calculate month-over-month change
      const currentMonth = new Date()?.getMonth();
      const currentYear = new Date()?.getFullYear();
      
      const currentMonthSpend = bookings?.filter(b => {
        const date = new Date(b?.created_at);
        return date?.getMonth() === currentMonth && date?.getFullYear() === currentYear;
      })?.reduce((sum, b) => sum + (b?.cost || 0), 0) || 0;

      const lastMonthSpend = bookings?.filter(b => {
        const date = new Date(b?.created_at);
        return date?.getMonth() === currentMonth - 1 && date?.getFullYear() === currentYear;
      })?.reduce((sum, b) => sum + (b?.cost || 0), 0) || 1;

      const changePercent = ((currentMonthSpend - lastMonthSpend) / lastMonthSpend * 100)?.toFixed(1);

      return {
        value: totalSpend,
        change: changePercent,
        changeType: changePercent >= 0 ? 'increase' : 'decrease',
        currentMonth: currentMonthSpend,
        lastMonth: lastMonthSpend,
        forecast: this.forecastSpend(bookings)
      };
    } catch (error) {
      console.error('Error fetching travel spend KPI:', error);
      return { value: 0, change: 0, changeType: 'neutral', currentMonth: 0, lastMonth: 0, forecast: [] };
    }
  },

  // Compliance Rates KPI
  async getComplianceRatesKPI() {
    try {
      const { data: compliance, error } = await supabase
        ?.from('travel_compliance')
        ?.select('compliance_status, created_at');

      if (error) throw error;

      const total = compliance?.length || 0;
      const compliant = compliance?.filter(c => c?.compliance_status === 'compliant')?.length || 0;
      const rate = total > 0 ? ((compliant / total) * 100)?.toFixed(1) : 0;

      // Calculate trend
      const currentMonth = new Date()?.getMonth();
      const currentYear = new Date()?.getFullYear();
      
      const currentMonthData = compliance?.filter(c => {
        const date = new Date(c?.created_at);
        return date?.getMonth() === currentMonth && date?.getFullYear() === currentYear;
      }) || [];

      const lastMonthData = compliance?.filter(c => {
        const date = new Date(c?.created_at);
        return date?.getMonth() === currentMonth - 1 && date?.getFullYear() === currentYear;
      }) || [];

      const currentRate = currentMonthData?.length > 0 
        ? (currentMonthData?.filter(c => c?.compliance_status === 'compliant')?.length / currentMonthData?.length * 100)
        : 0;
      
      const lastRate = lastMonthData?.length > 0
        ? (lastMonthData?.filter(c => c?.compliance_status === 'compliant')?.length / lastMonthData?.length * 100)
        : 0;

      const changePercent = (currentRate - lastRate)?.toFixed(1);

      return {
        value: parseFloat(rate),
        change: changePercent,
        changeType: changePercent >= 0 ? 'increase' : 'decrease',
        compliant,
        total,
        violations: total - compliant,
        trend: this.calculateComplianceTrend(compliance)
      };
    } catch (error) {
      console.error('Error fetching compliance rates KPI:', error);
      return { value: 0, change: 0, changeType: 'neutral', compliant: 0, total: 0, violations: 0, trend: [] };
    }
  },

  // Booking Trends KPI
  async getBookingTrendsKPI() {
    try {
      const { data: bookings, error } = await supabase
        ?.from('travel_bookings')
        ?.select('status, created_at, booking_type')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      const total = bookings?.length || 0;
      const confirmed = bookings?.filter(b => b?.status === 'confirmed')?.length || 0;
      const pending = bookings?.filter(b => b?.status === 'pending')?.length || 0;
      const cancelled = bookings?.filter(b => b?.status === 'cancelled')?.length || 0;

      // Calculate weekly trend
      const weeklyTrend = this.calculateWeeklyBookingTrend(bookings);
      const lastWeek = weeklyTrend?.[weeklyTrend?.length - 2]?.count || 0;
      const thisWeek = weeklyTrend?.[weeklyTrend?.length - 1]?.count || 0;
      const changePercent = lastWeek > 0 ? (((thisWeek - lastWeek) / lastWeek) * 100)?.toFixed(1) : 0;

      return {
        value: total,
        change: changePercent,
        changeType: changePercent >= 0 ? 'increase' : 'decrease',
        confirmed,
        pending,
        cancelled,
        weeklyTrend,
        byType: this.groupBookingsByType(bookings)
      };
    } catch (error) {
      console.error('Error fetching booking trends KPI:', error);
      return { value: 0, change: 0, changeType: 'neutral', confirmed: 0, pending: 0, cancelled: 0, weeklyTrend: [], byType: {} };
    }
  },

  // Budget Utilization KPI
  async getBudgetUtilizationKPI() {
    try {
      const { data: requests, error: requestsError } = await supabase
        ?.from('travel_requests')
        ?.select('estimated_budget, status, created_at');

      const { data: bookings, error: bookingsError } = await supabase
        ?.from('travel_bookings')
        ?.select('cost, status, created_at');

      if (requestsError || bookingsError) throw requestsError || bookingsError;

      const totalBudget = requests?.reduce((sum, r) => sum + (r?.estimated_budget || 0), 0) || 1;
      const totalSpent = bookings?.filter(b => b?.status === 'confirmed')?.reduce((sum, b) => sum + (b?.cost || 0), 0) || 0;
      const utilizationRate = ((totalSpent / totalBudget) * 100)?.toFixed(1);

      // Calculate monthly utilization trend
      const monthlyTrend = this.calculateMonthlyUtilization(requests, bookings);
      const lastMonth = monthlyTrend?.[monthlyTrend?.length - 2]?.utilization || 0;
      const thisMonth = monthlyTrend?.[monthlyTrend?.length - 1]?.utilization || 0;
      const changePercent = (thisMonth - lastMonth)?.toFixed(1);

      return {
        value: parseFloat(utilizationRate),
        change: changePercent,
        changeType: changePercent >= 0 ? 'increase' : 'decrease',
        totalBudget,
        totalSpent,
        remaining: totalBudget - totalSpent,
        monthlyTrend,
        forecast: this.forecastBudgetUtilization(monthlyTrend)
      };
    } catch (error) {
      console.error('Error fetching budget utilization KPI:', error);
      return { value: 0, change: 0, changeType: 'neutral', totalBudget: 0, totalSpent: 0, remaining: 0, monthlyTrend: [], forecast: [] };
    }
  },

  // Approval Processing Times KPI
  async getApprovalProcessingTimesKPI() {
    try {
      const { data: requests, error } = await supabase
        ?.from('travel_requests')
        ?.select('created_at, approved_at, status')
        ?.not('approved_at', 'is', null);

      if (error) throw error;

      // Calculate average processing time in hours
      const processingTimes = requests?.map(r => {
        const created = new Date(r?.created_at);
        const approved = new Date(r?.approved_at);
        return (approved - created) / (1000 * 60 * 60); // Convert to hours
      }) || [];

      const avgTime = processingTimes?.length > 0
        ? (processingTimes?.reduce((sum, time) => sum + time, 0) / processingTimes?.length)?.toFixed(1)
        : 0;

      // Calculate trend
      const currentMonth = new Date()?.getMonth();
      const currentYear = new Date()?.getFullYear();

      const currentMonthRequests = requests?.filter(r => {
        const date = new Date(r?.created_at);
        return date?.getMonth() === currentMonth && date?.getFullYear() === currentYear;
      }) || [];

      const lastMonthRequests = requests?.filter(r => {
        const date = new Date(r?.created_at);
        return date?.getMonth() === currentMonth - 1 && date?.getFullYear() === currentYear;
      }) || [];

      const currentAvg = this.calculateAvgProcessingTime(currentMonthRequests);
      const lastAvg = this.calculateAvgProcessingTime(lastMonthRequests);
      const changePercent = lastAvg > 0 ? (((currentAvg - lastAvg) / lastAvg) * 100)?.toFixed(1) : 0;

      return {
        value: parseFloat(avgTime),
        change: changePercent,
        changeType: changePercent <= 0 ? 'decrease' : 'increase', // Lower is better
        total: requests?.length || 0,
        fastest: Math.min(...processingTimes) || 0,
        slowest: Math.max(...processingTimes) || 0,
        trend: this.calculateProcessingTimeTrend(requests)
      };
    } catch (error) {
      console.error('Error fetching approval processing times KPI:', error);
      return { value: 0, change: 0, changeType: 'neutral', total: 0, fastest: 0, slowest: 0, trend: [] };
    }
  },

  // Helper: Calculate average processing time
  calculateAvgProcessingTime(requests) {
    if (!requests || requests?.length === 0) return 0;
    const times = requests?.map(r => {
      const created = new Date(r?.created_at);
      const approved = new Date(r?.approved_at);
      return (approved - created) / (1000 * 60 * 60);
    });
    return times?.reduce((sum, time) => sum + time, 0) / times?.length;
  },

  // Helper: Forecast spend based on historical data
  forecastSpend(bookings) {
    if (!bookings || bookings?.length === 0) return [];
    
    const monthlySpend = {};
    bookings?.forEach(booking => {
      const date = new Date(booking?.created_at);
      const monthKey = `${date?.getFullYear()}-${date?.getMonth() + 1}`;
      monthlySpend[monthKey] = (monthlySpend?.[monthKey] || 0) + (booking?.cost || 0);
    });

    const months = Object.keys(monthlySpend)?.sort();
    const values = months?.map(m => monthlySpend?.[m]);
    
    // Simple linear regression for next 3 months
    const avgGrowth = values?.length > 1 
      ? (values?.[values?.length - 1] - values?.[0]) / (values?.length - 1)
      : 0;

    const lastValue = values?.[values?.length - 1] || 0;
    const forecast = [];
    for (let i = 1; i <= 3; i++) {
      forecast?.push({
        month: `Month +${i}`,
        predicted: Math.max(0, lastValue + (avgGrowth * i))
      });
    }

    return forecast;
  },

  // Helper: Calculate compliance trend
  calculateComplianceTrend(compliance) {
    if (!compliance || compliance?.length === 0) return [];
    
    const monthlyData = {};
    compliance?.forEach(c => {
      const date = new Date(c?.created_at);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      if (!monthlyData?.[monthKey]) {
        monthlyData[monthKey] = { total: 0, compliant: 0 };
      }
      monthlyData[monthKey].total++;
      if (c?.compliance_status === 'compliant') {
        monthlyData[monthKey].compliant++;
      }
    });

    return Object.keys(monthlyData)?.sort()?.slice(-6)?.map(month => ({
      month: month?.substring(5),
      rate: ((monthlyData?.[month]?.compliant / monthlyData?.[month]?.total) * 100)?.toFixed(1)
    }));
  },

  // Helper: Calculate weekly booking trend
  calculateWeeklyBookingTrend(bookings) {
    if (!bookings || bookings?.length === 0) return [];
    
    const weeklyData = {};
    bookings?.forEach(booking => {
      const date = new Date(booking?.created_at);
      const weekStart = new Date(date);
      weekStart?.setDate(date?.getDate() - date?.getDay());
      const weekKey = weekStart?.toISOString()?.split('T')?.[0];
      weeklyData[weekKey] = (weeklyData?.[weekKey] || 0) + 1;
    });

    return Object.keys(weeklyData)?.sort()?.slice(-8)?.map(week => ({
      week,
      count: weeklyData?.[week]
    }));
  },

  // Helper: Group bookings by type
  groupBookingsByType(bookings) {
    if (!bookings || bookings?.length === 0) return {};
    
    const byType = {};
    bookings?.forEach(booking => {
      const type = booking?.booking_type || 'other';
      byType[type] = (byType?.[type] || 0) + 1;
    });
    return byType;
  },

  // Helper: Calculate monthly utilization
  calculateMonthlyUtilization(requests, bookings) {
    const monthlyData = {};
    
    requests?.forEach(r => {
      const date = new Date(r?.created_at);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      if (!monthlyData?.[monthKey]) {
        monthlyData[monthKey] = { budget: 0, spent: 0 };
      }
      monthlyData[monthKey].budget += r?.estimated_budget || 0;
    });

    bookings?.filter(b => b?.status === 'confirmed')?.forEach(b => {
      const date = new Date(b?.created_at);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      if (!monthlyData?.[monthKey]) {
        monthlyData[monthKey] = { budget: 0, spent: 0 };
      }
      monthlyData[monthKey].spent += b?.cost || 0;
    });

    return Object.keys(monthlyData)?.sort()?.slice(-6)?.map(month => ({
      month: month?.substring(5),
      budget: monthlyData?.[month]?.budget,
      spent: monthlyData?.[month]?.spent,
      utilization: monthlyData?.[month]?.budget > 0 
        ? ((monthlyData?.[month]?.spent / monthlyData?.[month]?.budget) * 100)?.toFixed(1)
        : 0
    }));
  },

  // Helper: Forecast budget utilization
  forecastBudgetUtilization(monthlyTrend) {
    if (!monthlyTrend || monthlyTrend?.length === 0) return [];
    
    const values = monthlyTrend?.map(m => parseFloat(m?.utilization));
    const avgGrowth = values?.length > 1
      ? (values?.[values?.length - 1] - values?.[0]) / (values?.length - 1)
      : 0;

    const lastValue = values?.[values?.length - 1] || 0;
    const forecast = [];
    for (let i = 1; i <= 3; i++) {
      forecast?.push({
        month: `Month +${i}`,
        predicted: Math.min(100, Math.max(0, lastValue + (avgGrowth * i)))
      });
    }

    return forecast;
  },

  // Helper: Calculate processing time trend
  calculateProcessingTimeTrend(requests) {
    if (!requests || requests?.length === 0) return [];
    
    const monthlyData = {};
    requests?.forEach(r => {
      const date = new Date(r?.created_at);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      if (!monthlyData?.[monthKey]) {
        monthlyData[monthKey] = { times: [] };
      }
      const created = new Date(r?.created_at);
      const approved = new Date(r?.approved_at);
      const hours = (approved - created) / (1000 * 60 * 60);
      monthlyData?.[monthKey]?.times?.push(hours);
    });

    return Object.keys(monthlyData)?.sort()?.slice(-6)?.map(month => {
      const times = monthlyData?.[month]?.times;
      const avg = times?.reduce((sum, t) => sum + t, 0) / times?.length;
      return {
        month: month?.substring(5),
        avgTime: avg?.toFixed(1)
      };
    });
  },

  // Subscribe to real-time KPI updates
  subscribeToKPIUpdates(callback) {
    const channel = supabase?.channel('kpi-updates')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'travel_bookings' }, () => {
        callback('travel_bookings');
      })
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'travel_requests' }, () => {
        callback('travel_requests');
      })
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'travel_compliance' }, () => {
        callback('travel_compliance');
      })
      ?.subscribe();

    return channel;
  },

  // Unsubscribe from real-time updates
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};